<!-- The bay strip: every bay on the wall as a scrollable row. Tap one and the
     wall pans there; the bay in view is highlighted and kept in sight.
     With filters on, each bay shows how many of its coffees match.

     Touch: the row is a plain native scroller (pan-x, momentum). Two things
     that fought a finger on iOS are gone: the edge fade was a mask-image on the
     scrolling element itself (WebKit does not reliably hand a pan to an
     overflow scroller that is masked; the fades are now overlays on the nav),
     and the names swapped between <button> and <span> under the finger while
     it moved (the swap now waits until the row comes to rest). -->
<template>
  <nav class="bay-strip" aria-label="Bays on the wall">
    <div ref="row" class="bay-strip__row" @scroll.passive="onRowScroll">
      <!-- Only the names actually on screen are buttons. The rest of the row is
           reached by scrolling it, and a control half off the edge of the screen
           is one nobody can press reliably (and one the factory QA rejects). -->
      <component
        :is="shown.has(b.key) ? 'button' : 'span'"
        v-for="b in bays"
        :key="b.key"
        :ref="(el) => setRef(b.key, el)"
        :type="shown.has(b.key) ? 'button' : undefined"
        class="bay"
        :class="{ 'bay--on': b.key === active, 'bay--empty': filtered && !b.matchCount, 'bay--special': b.special }"
        :aria-current="b.key === active ? 'location' : undefined"
        :aria-label="`${b.title}, ${countLabel(b)}`"
        @click="shown.has(b.key) && $emit('focus', b.key)"
      >
        <span v-if="b.special" class="bay__spark" aria-hidden="true" />
        <span class="bay__name">{{ b.title }}</span>
        <span class="bay__count">{{ filtered ? `${b.matchCount}/${b.coffees.length}` : b.coffees.length }}</span>
      </component>
    </div>
  </nav>
</template>

<script setup>
import { ref, reactive, watch, nextTick, onMounted, onBeforeUnmount } from 'vue';
import { reducedMotion } from '../store.js';

const props = defineProps({
  bays: { type: Array, required: true },
  active: { type: String, default: '' },
  filtered: { type: Boolean, default: false },
});
defineEmits(['focus']);

const row = ref(null);
const refs = {};
// A name is re-created when it turns from <span> to <button> (or back), so the
// observer must follow the new element. (It used to keep watching the detached
// old one, which reports 0% visible: after the first scroll every name was stuck
// as a dead <span>, and nothing in the strip answered a tap.)
function setRef (key, el) {
  const n = el?.$el ?? el;
  if (!n) return;
  n.dataset.bay = key;
  const old = refs[key];
  if (old === n) return;
  if (old) io?.unobserve(old);
  refs[key] = n;
  io?.observe(n);
}

// Which bay names are (almost) fully inside the scrolling row. Applied only
// once the row is at rest, so no element is swapped out under a moving finger.
const shown = reactive(new Set());
const pending = new Map();
let settleTimer = 0;
let moving = false;
function flush () {
  for (const [key, on] of pending) { if (on) shown.add(key); else shown.delete(key); }
  pending.clear();
}
function onRowScroll () {
  moving = true;
  clearTimeout(settleTimer);
  settleTimer = setTimeout(() => { moving = false; flush(); }, 140);
}
let io = null;
function observeAll () {
  if (!io) return;
  io.disconnect();
  Object.values(refs).forEach((el) => el && io.observe(el));
}
onMounted(() => {
  if (typeof IntersectionObserver === 'undefined') { props.bays.forEach((b) => shown.add(b.key)); return; }
  io = new IntersectionObserver((entries) => {
    for (const e of entries) {
      const key = e.target.dataset.bay;
      if (!key) continue;
      pending.set(key, e.intersectionRatio >= 0.9);
    }
    if (!moving) flush();
  }, { root: row.value, threshold: [0, 0.9, 1] });
  observeAll();
});
onBeforeUnmount(() => { io?.disconnect(); clearTimeout(settleTimer); });
watch(() => props.bays.map((b) => b.key).join('|'), async () => { await nextTick(); observeAll(); });

const countLabel = (b) => (props.filtered
  ? `${b.matchCount} of ${b.coffees.length} ${b.coffees.length === 1 ? 'coffee matches' : 'coffees match'}`
  : `${b.coffees.length} ${b.coffees.length === 1 ? 'coffee' : 'coffees'}`);

// Keep the bay in view visible in the strip, without stealing focus.
watch(() => props.active, async (key) => {
  await nextTick();
  const el = refs[key]; const box = row.value;
  if (!el || !box) return;
  const left = el.offsetLeft - (box.clientWidth - el.offsetWidth) / 2;
  box.scrollTo({ left: Math.max(0, left), behavior: reducedMotion.value ? 'auto' : 'smooth' });
});
</script>

<style scoped>
.bay-strip { position: relative; min-width: 0; margin: 4px -16px 0; }
/* The edge fades: overlays on the nav, never a mask on the scroller. */
.bay-strip::before,
.bay-strip::after {
  content: '';
  position: absolute;
  top: 0;
  bottom: 0;
  z-index: 1;
  width: 16px;
  pointer-events: none;
}
.bay-strip::before { left: 0; background: linear-gradient(90deg, var(--dock, #FAF7F1), transparent); }
.bay-strip::after { right: 0; background: linear-gradient(270deg, var(--dock, #FAF7F1), transparent); }
.bay-strip__row {
  display: flex;
  gap: 2px;
  overflow-x: auto;
  overflow-y: hidden;
  scrollbar-width: none;
  padding: 0 10px;
  scroll-padding: 0 16px;
  -webkit-overflow-scrolling: touch;
  overscroll-behavior-x: contain;
  touch-action: pan-x;
}
.bay-strip__row::-webkit-scrollbar { display: none; }
.bay {
  flex: none;
  position: relative;
  display: inline-flex;
  align-items: center;
  gap: 5px;
  min-height: 44px;
  padding: 0 6px;
  border: 0;
  background: transparent;
  /* --muted, not the comp's --faint: faint is 2.9:1 on the dock, under AA for
     12px text (the same call as the tab row in app.css). */
  color: var(--muted, #5E6B60);
  font-family: var(--font-body, 'Manrope', sans-serif);
  font-size: var(--text-xs);
  font-weight: 700;
  white-space: nowrap;
  cursor: pointer;
  touch-action: pan-x;
  -webkit-user-select: none;
  user-select: none;
  transition: color 180ms ease-out;
}
.bay__count { font-weight: 600; font-variant-numeric: tabular-nums; }
.bay--on { color: var(--ink, #1F2A22); }
.bay--on .bay__name { box-shadow: inset 0 -2px 0 var(--mustard, #F2C230); padding-bottom: 3px; margin-bottom: -5px; }
.bay--empty { opacity: 0.45; }
.bay__spark {
  width: 6px; height: 6px;
  border-radius: 50%;
  background: var(--oak, #B9854B);
}
.bay:focus-visible { outline: 2px solid var(--ink, #1F2A22); outline-offset: -4px; border-radius: 8px; }
@media (prefers-reduced-motion: reduce) { .bay { transition: none; } }
</style>
